// Path: ./src/utils/api.ts
/**
 * This service implements functionality to make api calls through open api generated client
 * We are adding custom axios instance which helps customize the generated client with interceptors and more axios functionalities
 */
import {
  AdminApi,
  AdminApiFetchParamCreator,
  AdminFlyerReviewApi,
  GfMultiImage,
  Configuration as AdminConfiguration,
} from "@swagger/typescript-fetch-goflyer";
import { DecorateAll } from "decorate-all";
// Configuration and base path are not provided

import {
  DefaultApi as SharpDefaultApi,
  Configuration,
} from "@swagger/typescript-fetch-sharp-processor";
import { toastError } from "./pageUtil/toast";
// @ts-ignore
import deepForEach from "deep-for-each";
import { setTimeBackToLocalDate } from "./dateUtil/setTimeBackToLocalDate";
import { instanceOfFlyerItem } from "./utils";
import {
  isBearerToken,
  readApiError,
  sentBearerToken,
  withBearerToken,
} from "./apiError";
import { getAuthToken } from "./authTokenStorage";
import { waitForReauth } from "./sessionExpiry";

console.log(
  "process.env.REACT_APP_GOFLYER_API_END_POINT",
  process.env.REACT_APP_SHARP_SERVER_URL,
);

const sharpConfig = new Configuration({
  basePath: process.env.REACT_APP_SHARP_SERVER_URL,
});

const sharpApi = new SharpDefaultApi(sharpConfig);

/**
 * Fixes up API payloads in place: converts date fields back to local time
 * and fills `gfMultiImages` on flyer items that only have `gfImages`.
 */
export const normalizeApiResult = <T>(result: T): T => {
  // @ts-ignore
  deepForEach(result, (value, key, subject) => {
    if (subject.validStartDate) {
      subject.validStartDate = setTimeBackToLocalDate(
        subject.validStartDate,
      );
    }
    if (subject.validEndDate) {
      subject.validEndDate = setTimeBackToLocalDate(subject.validEndDate);
    }
    if (subject.createdAt) {
      subject.createdAt = setTimeBackToLocalDate(subject.createdAt);
    }
    if (subject.updatedAt) {
      subject.updatedAt = setTimeBackToLocalDate(subject.updatedAt);
    }

    if (instanceOfFlyerItem(subject)) {
      if (!subject?.gfMultiImages?.length && subject.gfImages?.length) {
        subject.gfMultiImages = subject.gfImages?.map((obj) => {
          const {
            id,
            isDeleted,
            gfFlyerItems: _gfFlyerItems,
            gfMultiImage: _gfMultiImage,
            ...val
          } = obj;
          return {
            id,
            isDeleted,
            gfFlyerItem: { id: subject.id },
            gfImage: {
              ...val,
              src: val.src,
              id,
              isDeleted,
            },
          };
        }) as GfMultiImage[];
      }
    }
  });
  return result;
};

type SdkFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** Responses to requests that went out with a non-empty Bearer token. */
const responsesSentWithToken = new WeakSet<Response>();

/**
 * True when the request behind a failed SDK call carried a non-empty Bearer
 * token, whether the caller passed it or `fetchWithAuthToken` added it.
 * The SDK throws the fetch's own `Response` for non-2xx answers.
 */
export const responseSentWithToken = (error: unknown): boolean =>
  error instanceof Response && responsesSentWithToken.has(error);

/**
 * Fetch for the SDK clients. Sends the stored admin login token unless the
 * call already passed a non-empty `Authorization` (an empty "Bearer " counts
 * as none). With no stored token the request goes out as the call built it,
 * so sign-in and the other public routes work before login.
 */
const fetchWithAuthToken: SdkFetch = (url, init) => {
  const headers = new Headers(init?.headers);
  if (!isBearerToken(headers.get("Authorization"))) {
    const token = getAuthToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  const sentToken = isBearerToken(headers.get("Authorization"));
  return fetch(url, { ...init, headers }).then((response) => {
    if (sentToken) {
      responsesSentWithToken.add(response);
    }
    return response;
  });
};

/** `fetchWithAuthToken` plus the `X-Route-To` header for the admin API. */
const fetchWithRouteHeader: SdkFetch = (url, init) => {
  const headers = new Headers(init?.headers);
  headers.set("X-Route-To", "9002");
  return fetchWithAuthToken(url, { ...init, headers });
};

export const globalCatchApiError = (
  target: any,
  propertyKey: string,
  descriptor: PropertyDescriptor,
) => {
  const original = descriptor.value;
  const wrapped = async function (this: unknown, ...args: any[]): Promise<any> {
    try {
      const result = await original.apply(this, args);
      normalizeApiResult(result);
      return result;
    } catch (error: unknown) {
      const { status, message } = await readApiError(error);

      // Expired/invalid login: ask the user to sign in again, then replay the
      // same call with the new token. Resolves undefined if they sign out.
      // A call that didn't pass a token is replayed as-is and picks up the
      // new one from the token store.
      if (
        status === 401 &&
        (sentBearerToken(args) || responseSentWithToken(error))
      ) {
        const newToken = await waitForReauth();
        return newToken
          ? wrapped.apply(this, withBearerToken(args, newToken))
          : undefined;
      }

      console.error(`API ${propertyKey} failed`, error);
      toastError(`Error: ${message}`);
    }
  };
  descriptor.value = wrapped;
};

// @ts-ignore
@DecorateAll(globalCatchApiError, { deep: true })
class DecoratedAdminApi extends AdminApi {}

const adminApiService = new DecoratedAdminApi(
  undefined,
  process.env.REACT_APP_GOFLYER_API_END_POINT,
  fetchWithRouteHeader,
);

/**
 * Undecorated AdminApi instance.
 *
 * `adminApiService` is wrapped by `@DecorateAll(globalCatchApiError)`, which
 * swallows errors (returning `undefined`) and pops a generic toast. That
 * breaks react-query's error/abort handling for the Flyer Review queries
 * (an aborted fetch would surface as a spurious toast + `undefined` result).
 * This raw client uses the same fetch (`X-Route-To` header and login token)
 * but lets rejections (including `AbortError`) propagate so react-query can
 * manage them. It is used by the Flyer Review query hooks, and by the auth
 * calls (token check, session-expired sign-in) that must see the real HTTP
 * status instead of the decorator's 401 handling.
 */
const adminApiServiceRaw = new AdminApi(
  undefined,
  process.env.REACT_APP_GOFLYER_API_END_POINT,
  fetchWithRouteHeader,
);

const cfg = new AdminConfiguration({
  basePath: process.env.REACT_APP_GOFLYER_API_END_POINT,
});
const paramCreator = AdminApiFetchParamCreator(cfg);

/** Flyer review admin HTTP API (bootstrap, audiences, CRs, comments). */
const reviewApiService = new AdminFlyerReviewApi(
  undefined,
  process.env.REACT_APP_GOFLYER_API_END_POINT,
  fetchWithAuthToken,
);

export {
  adminApiService,
  adminApiServiceRaw,
  fetchWithRouteHeader,
  sharpApi,
  paramCreator,
  reviewApiService,
};
